import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { FormContext } from '../Form/Form.js';
import Input from '../Input/Input.js';
import { isRequired, maxLength } from '../Util/validations.js';
import FormControlFieldWrapper from './FormControlWrapper.js';

const isEqualState = (a, b)=>a.value === b.value && a.isTouched === b.isTouched && a.isValid === b.isValid && a.readonly === b.readonly
;
class FormControl extends (React.Component) {
    static getDerivedStateFromProps(props, state) {
        const { isValid , errorMessage  } = FormControl.validate(props.validations, props.validationsWithErrorMessages, props.required || false, state.value || '', state.errorMessage, props.maxLength);
        return {
            isValid,
            errorMessage
        };
    }
    componentDidMount() {
        const initialState = this.props.initialState;
        if (initialState) {
            const { isValid , errorMessage  } = FormControl.validate(this.props.validations, this.props.validationsWithErrorMessages, this.props.required || false, initialState.value, this.state.errorMessage);
            this.setState({
                value: initialState.value || '',
                isTouched: initialState.isTouched !== undefined && initialState.isTouched,
                isValid,
                errorMessage
            });
            if (initialState.readonly) {
                this.setState({
                    readonly: true
                });
            }
        }
    }
    applyInitialState(initialState) {
        const { isValid , errorMessage  } = FormControl.validate(this.props.validations, this.props.validationsWithErrorMessages, this.props.required || false, initialState.value, this.props.errorMessage);
        this.setState({
            value: initialState.value || '',
            isTouched: initialState.isTouched !== undefined && initialState.isTouched,
            isValid,
            errorMessage
        });
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.initialState && this.props.initialState && !isEqualState(prevProps.initialState, this.props.initialState)) {
            this.applyInitialState(this.props.initialState);
        }
        if (prevState.isTouched !== this.state.isTouched || prevState.isValid !== this.state.isValid || prevState.value !== this.state.value) {
            this.onChange();
        }
    }
    render() {
        const _props = this.props, { label , id , type , icon , errorMessage , required , placeholder , optionalText , tooltip , initialState , disabled , className , maxLength: maxLength1 , hideAsteriskSign  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "label",
            "id",
            "type",
            "icon",
            "errorMessage",
            "required",
            "placeholder",
            "optionalText",
            "tooltip",
            "initialState",
            "disabled",
            "className",
            "maxLength",
            "hideAsteriskSign"
        ]);
        const { isValid , isTouched , value , readonly  } = this.state;
        const displayValid = isValid === true && isTouched === true && value !== '' && !readonly;
        const displayError = isValid === false && isTouched === true && !readonly;
        const errorMessageId = `${id}-error-message`;
        const ariaDescribedBy = `${rest['aria-describedby'] || ''} ${errorMessageId}`.trim();
        const inputClasses = classnames('form-control__input', {
            'form-control__input--with-aside': icon,
            _is_valid: displayValid,
            _is_invalid: displayError
        });
        return /*#__PURE__*/ React.createElement(FormControlFieldWrapper, swcHelpers.extends({}, {
            className,
            label,
            required,
            hideAsteriskSign,
            tooltip,
            id,
            optionalText,
            errorMessageId,
            errorMessage: this.state.errorMessage,
            displayError
        }), /*#__PURE__*/ React.createElement(Input, {
            "aria-invalid": !isValid,
            "aria-required": required,
            "aria-describedby": ariaDescribedBy,
            id: id,
            className: inputClasses,
            "data-t": this.props['data-t'],
            type: type,
            initialValue: initialState && initialState.value || '',
            placeholder: placeholder,
            onChange: this.handleChange,
            onBlur: this.handleBlur,
            disabled: disabled || readonly,
            maxLength: maxLength1
        }), icon && /*#__PURE__*/ React.createElement("div", {
            className: "form-control__aside"
        }, icon));
    }
    constructor(...args){
        super(...args);
        this.state = {
            name: this.props.id,
            errorMessage: this.props.errorMessage
        };
        this.onChange = ()=>{
            const { isValid , errorMessage  } = FormControl.validate(this.props.validations, this.props.validationsWithErrorMessages, this.props.required, this.state.value, this.state.errorMessage);
            if (this.props.onChange) {
                this.props.onChange(swcHelpers.objectSpread({}, this.state, {
                    isValid,
                    errorMessage
                }));
            }
        };
        this.handleChange = (state)=>{
            const value = state.value;
            if (value !== this.state.value) {
                this.setState({
                    value
                });
            }
        };
        this.handleBlur = (state)=>{
            const value = state.value.trim();
            this.setState({
                isTouched: state.isTouched,
                value
            }, ()=>{
                var _props, ref;
                (ref = (_props = this.props).onBlur) === null || ref === void 0 ? void 0 : ref.call(_props, this.state);
            });
        };
    }
}
FormControl.defaultProps = {
    type: 'text',
    hideAsteriskSign: false,
    required: true,
    validations: [],
    initialState: {
        value: '',
        isTouched: false
    },
    disabled: false
};
FormControl.validate = (validations, validationsWithErrorMessages = [], required, value, initialErrorMessage, maxLengthValue)=>{
    let errorMessage = initialErrorMessage !== null && initialErrorMessage !== void 0 ? initialErrorMessage : '';
    if (!required && value.length === 0) {
        return {
            isValid: true,
            errorMessage
        };
    }
    if (required) {
        if (!isRequired(value)) {
            return {
                isValid: false,
                errorMessage
            };
        }
    }
    if (maxLengthValue && !maxLength(maxLengthValue)(value)) {
        return {
            isValid: false,
            errorMessage
        };
    }
    const validationsAreMet = validations.reduce((isValid, validator)=>validator(value) && isValid
    , true);
    let validationsWithErrorMessagesAreMet = true;
    for (const validatorWithErrorMessage of validationsWithErrorMessages){
        const isValid = validatorWithErrorMessage.validator(value);
        validationsWithErrorMessagesAreMet = isValid;
        if (!isValid) {
            errorMessage = validatorWithErrorMessage.errorMessage;
            break;
        }
    }
    return {
        isValid: validationsAreMet && validationsWithErrorMessagesAreMet,
        errorMessage
    };
};
const ConnectedFormControl = (_param)=> {
    var { onBlur , onChange  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "onBlur",
        "onChange"
    ]);
    return React.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React.createElement(FormControl, swcHelpers.extends({}, rest, {
            onBlur: context.onBlur || onBlur,
            onChange: context.onChange || onChange,
            initialState: context.getInitialState && context.getInitialState(rest.id) || rest.initialState && rest.initialState
        }))
    );
};
ConnectedFormControl.defaultProps = swcHelpers.objectSpread({}, FormControl.defaultProps);

export { FormControl, ConnectedFormControl as default };
//# sourceMappingURL=FormControl.js.map
